import { Injectable, inject, computed, effect } from '@angular/core'
import { Router, Params } from '@angular/router'

import { HydrationService } from './hydration.service'
import { SessionService } from './session.service'
import {
  QueryParamValue,
  QueryParams,
  SelectedQueryPath,
  QueryClickEvent,
} from './utils/navigation.utils'

/**
 * NavigationService centralizes all URL + queryParam reads/writes,
 * and ensures that any navigation only fires once the current
 * Hunt has finished loading in SessionService.
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly router = inject(Router)
  private readonly hydration = inject(HydrationService)
  private readonly session = inject(SessionService)
  // ─── URL PARAMS AS SIGNALS ────────────────────────────────────────────────────

  /** current :id from the URL (always up-to-date) */
  readonly huntId = this.hydration.param('id')

  /** selected sub-tab on the Hunt Package details page */
  readonly huntPackageSubTab = this.hydration.query('subtab', 'overview')

  /** sub-route within “edit-report” */
  readonly selectedEditReportSection = computed<string>(() => {
    this.hydration.leafSnapshot()
    const parts = this.router.url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)
    const idx = parts.indexOf('edit-report')
    const sec = parts[idx + 1]
    const pkg = parts[idx + 2]
    if (sec === 'overview') return 'overview'
    if (sec === 'conclusion') return 'conclusion'
    if (sec === 'technical-summary' && pkg) return pkg
    return 'overview'
  })

  /** sub-route within “final-report” */
  readonly selectedFinalReportSection = computed<string>(() => {
    this.hydration.leafSnapshot()

    const parts = this.router.url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter(Boolean)
    const idx = parts.indexOf('final-report')
    const sec = parts[idx + 1]
    const pkg = parts[idx + 2]
    if (sec === 'overview') return 'overview'
    if (sec === 'conclusion') return 'conclusion'
    if (sec === 'coverage') return 'coverage'
    if (sec === 'technical-summary' && pkg) return pkg
    return 'overview'
  })

  /** all other path- and query-params via HydrationService */
  readonly packageId = this.hydration.param('packageId')
  readonly environmentId = this.hydration.query('environmentId')
  readonly category = this.hydration.query('category')
  readonly toolId = this.hydration.query('toolId')
  readonly queryId = this.hydration.query('queryId')
  readonly selectedQueryId = this.hydration.query('selectedQueryId')
  readonly pivotPath = this.hydration.arrayQuery('pivotPath')
  readonly pivotTab = this.hydration.query('pivotTab', 'overview')
  readonly showCaptureDialog = this.hydration.boolQuery('showCaptureDialog')
  readonly showTuning = this.hydration.boolQuery('showTuning')
  readonly pivotStep = this.hydration.query('step', 'select')
  readonly chooseYourOwn = this.hydration.boolQuery('chooseYourOwn')

  /** which “tab” of the edit-report view */
  readonly reportTab = this.hydration.query('tab', 'overview')
  readonly reportSubTab = this.hydration.query('subtab', 'overview')

  /** which “tab” of the final-report view */
  readonly finalTab = this.hydration.query('tab', 'overview')
  readonly finalSubTab = this.hydration.query('subtab', 'overview')

  /** parsed huntQueryId + pivotIds from URL */
  readonly parsedSelectedQuery = computed<SelectedQueryPath | null>(() => {
    const sel = this.selectedQueryId()
    const tool = this.toolId()
    return sel && tool
      ? { toolId: tool, huntQueryId: sel, pivotIds: this.pivotPath() }
      : null
  })

  // ─── QUERY PARAM HELPERS ──────────────────────────────────────────────────────

  setQueryParam(name: string, value: QueryParamValue) {
    this.router.navigate([], {
      queryParams: { [name]: value },
      queryParamsHandling: 'merge',
    })
  }

  mergeQueryParams(params: QueryParams) {
    const normalized = Object.fromEntries(
      Object.entries(params).map(([k, v]) => [
        k,
        Array.isArray(v) ? v.join(',') : v,
      ]),
    )
    this.router.navigate([], {
      queryParams: normalized,
      queryParamsHandling: 'merge',
    })
  }

  setHuntPackageSubTab(tab: string) {
    this.setQueryParam('subtab', tab)
  }

  clearQueryParams(keys: string[]) {
    const cleared: QueryParams = {}
    keys.forEach((k) => (cleared[k] = null))
    this.router.navigate([], {
      queryParams: cleared,
      queryParamsHandling: 'merge',
    })
  }

  // ─── GUARD NAVIGATION UNTIL HUNT LOADED ───────────────────────────────────────

  /** Only invoke cb once session.loading() is false */
  private whenLoaded(cb: () => void) {
    if (!this.session.loading()) {
      cb()
      return
    }
    const stop = effect(() => {
      if (!this.session.loading()) {
        queueMicrotask(cb)
        stop.destroy()
      }
    })
  }

  // ─── TOP-LEVEL ROUTES ──────────────────────────────────────────────────────────

  navigateToHuntList() {
    this.whenLoaded(() => this.router.navigate(['/hunt', 'hunts']))
  }

  navigateToWorkbench() {
    this.whenLoaded(() =>
      this.router.navigate(['/hunt', this.huntId(), 'workbench']),
    )
  }

  navigateToEditReport(segments: string[] = ['overview']) {
    this.whenLoaded(() =>
      this.router.navigate([
        '/hunt',
        this.huntId(),
        'edit-report',
        ...segments,
      ]),
    )
  }

  navigateToEditReportOverview() {
    this.navigateToEditReport(['overview'])
  }

  navigateToEditReportTechSummary(pkgId: string) {
    this.navigateToEditReport(['technical-summary', pkgId])
  }

  navigateToFinalReportTechSummary(pkgId: string) {
    this.navigateToFinalReport(['technical-summary', pkgId])
  }

  navigateToEditReportConclusion() {
    this.navigateToEditReport(['conclusion'])
  }

  navigateToFinalReport(
    segments: string[] = ['overview'],
    queryParams?: Params,
  ) {
    this.whenLoaded(() => {
      const id = this.huntId()
      if (!id) {
        console.error('NavigationService.navigateToFinalReport', 'no huntId')
        return
      }
      this.router.navigate(
        ['hunt', id, 'final-report', ...segments],
        queryParams ? { queryParams } : undefined,
      )
    })
  }

  navigateToFinalOverview() {
    this.navigateToFinalReport(['overview'])
  }

  navigateToFinalConclusion() {
    this.navigateToFinalReport(['conclusion'])
  }

  navigateToFinalCoverage() {
    this.navigateToFinalReport(['coverage'])
  }

  navigateToHuntPackage(pkgId: string) {
    this.whenLoaded(() =>
      this.router.navigate([
        '/hunt',
        this.huntId(),
        'workbench',
        'package',
        pkgId,
      ]),
    )
  }

  /** Navigate to a threat profile detail page */
  navigateToThreatProfile(uuid: string) {
    this.whenLoaded(() => this.router.navigate(['/threat-profiles', uuid]))
  }

  // ─── PIVOTED QUERY HELPERS ────────────────────────────────────────────────────

  pushPivot(pivotId: string) {
    const updated = [...this.pivotPath(), pivotId]
    this.mergeQueryParams({ pivotPath: updated })
  }

  popPivot() {
    const updated = this.pivotPath().slice(0, -1)
    this.setQueryParam('pivotPath', updated.length ? updated : null)
  }

  setPivotTab(tab: string) {
    this.setQueryParam('pivotTab', tab)
  }

  setSelectedEnvironmentId(envId: string) {
    this.setQueryParam('environmentId', envId)
  }

  setSelectedQuery(
    toolId: string,
    huntQueryId: string,
    pivotPath: string[] = [],
  ) {
    // const sel = buildSelectedQueryId(huntQueryId, pivotPath)

    this.mergeQueryParams({
      toolId,
      selectedQueryId: huntQueryId,
      pivotPath: pivotPath.length ? pivotPath : null,
    })
  }

  clearDrilldown() {
    this.clearQueryParams([
      'environmentId',
      'toolId',
      'queryId',
      'selectedQueryId',
      'pivotPath',
      'pivotTab',
    ])
  }

  setSelectedPivotTab(tab: 'overview' | 'deployment' | 'emulation') {
    this.whenLoaded(() => this.setQueryParam('pivotTab', tab))
  }

  handleQueryClick(event: QueryClickEvent) {
    const isPivot = event.type === 'pivot' && !!event.pivotPath?.length
    this.mergeQueryParams({
      toolId: event.toolId,
      selectedQueryId: event.queryId,
      pivotPath: isPivot ? event.pivotPath : null,
      pivotTab: isPivot ? 'overview' : null,
    })
  }

  // ─── UTILITY ─────────────────────────────────────────────────────────────────

  /** Which top‐level tab are we on? */
  currentTab(): string {
    const segs = this.router.url.split('/').filter(Boolean)
    const id = segs[1]
    const idx = segs.indexOf(id)
    return idx >= 0 && segs.length > idx + 1 ? segs[idx + 1] : 'workbench'
  }
}
